/* eslint-disable max-lines */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { EMPTY, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import type { Attachment } from '@core/attachments-types';
import { AttachmentItemComponent } from '@core/attachments-ui';
import { fileToBase64, filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  CheckboxComponent,
  DividerComponent,
  FormFieldComponent,
  IconComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  RtStatefulComponent,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';

import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { SimpleMultiselectComponent } from '../../../misc/simple-multiselect/simple-multiselect.component';
import type { Location, Posting } from '../types';
import { WerkenInEenHotelApiService } from '../werken-in-een-hotel-api';

type Resource = {
  label: string;
  value: string;
};

export const initializeEmptyPostingFormGroup = (): UntypedFormGroup =>
  new UntypedFormGroup({
    locationId: new UntypedFormControl(null, Validators.required),
    contactId: new UntypedFormControl(null, Validators.required),
    functionCharacteristicId: new UntypedFormControl(null, Validators.required),
    employmentIds: new UntypedFormControl([], Validators.required),
    mainFunctionCategoryId: new UntypedFormControl(null, Validators.required),
    extraFunctionCategoryId: new UntypedFormControl(null),
    publishOnOtherJobBoards: new UntypedFormControl(false, Validators.required),
    youtubeUrl: new UntypedFormControl(''),
    mainVacancyPhoto: new UntypedFormControl(null),
    additionalPhotosToDrop: new UntypedFormControl([]),
    additionalPhotosToAdd: new UntypedFormControl([]),
  });

export const initializePostingFormGroup = (posting: Posting): UntypedFormGroup =>
  new UntypedFormGroup({
    locationId: new UntypedFormControl(
      { value: posting.locationId, disabled: true },
      Validators.required,
    ),
    contactId: new UntypedFormControl(posting.contactId, Validators.required),
    functionCharacteristicId: new UntypedFormControl(
      posting.functionCharacteristicId,
      Validators.required,
    ),
    employmentIds: new UntypedFormControl(posting.employmentIds, Validators.required),
    mainFunctionCategoryId: new UntypedFormControl(
      posting.mainFunctionCategoryId,
      Validators.required,
    ),
    extraFunctionCategoryId: new UntypedFormControl(posting.extraFunctionCategoryId),
    publishOnOtherJobBoards: new UntypedFormControl(
      posting.publishOnOtherJobBoards,
      Validators.required,
    ),
    youtubeUrl: new UntypedFormControl(posting.youtubeUrl),
    mainVacancyPhoto: new UntypedFormControl(null),
    additionalPhotosToDrop: new UntypedFormControl([]),
    additionalPhotosToAdd: new UntypedFormControl([]),
  });

type State = {
  mainVacancyPhotoAttachment: Attachment | null;
  draftAdditionalPhotoAttachments: Attachment[];
  locations: Location[] | null;
  employmentTypeQuery: string;
  contacts: Resource[];
  employmentTypes: Resource[];
  features: Resource[];
  jobCategories: Resource[];
  errors: string[];
};

@Component({
  selector: 'rti-werken-in-een-hotel-posting-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './werken-in-een-hotel-posting-form.component.html',
  styleUrls: ['./werken-in-een-hotel-posting-form.component.less'],
  imports: [
    AttachmentItemComponent,
    ButtonComponent,
    CheckboxComponent,
    DividerComponent,
    ErrorMessagesWrapperComponent,
    FormFieldComponent,
    IconComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    ReactiveFormsModule,
    RtTooltip,
    SelectComponent,
    SimpleMultiselectComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class WerkenInEenHotelPostingFormComponent extends RtStatefulComponent<State> {
  private readonly werkenInEenHotelApi = inject(WerkenInEenHotelApiService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);

  public readonly posting = input<Posting | null>(undefined!);
  public readonly postingForm = input<UntypedFormGroup>(undefined!);
  public readonly errorFields = input<{
    [field: string]: string[];
  }>(undefined!);

  public emptyEmploymentIdsControl: UntypedFormControl = new UntypedFormControl([]);

  private MAX_ADDITIONAL_PHOTO_COUNT: number = 3;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);

    this.createState({
      mainVacancyPhotoAttachment: null,
      draftAdditionalPhotoAttachments: [],
      locations: null,
      employmentTypeQuery: '',
      contacts: [],
      employmentTypes: [],
      features: [],
      jobCategories: [],
      errors: [],
    });
  }

  public ngOnInit(): void {
    this.initLocations();
    this.reloadResources();
    this.generatePhotoPreviews();

    this.postingForm()
      .get('locationId')!
      .valueChanges.pipe(takeUntilDestroyed(this))
      .subscribe(_locationId => {
        this.reloadResources();
        this.postingForm().get('contactId')!.setValue(null);
      });
  }

  public get locationOptions(): Array<SelectOption<number>> {
    return (this.state.locations || []).map(location => ({
      label: location.name,
      value: location.id,
    }));
  }

  public mainVacancyPhotoInputChanged(fileList: FileList | null): void {
    if (fileList?.[0]) {
      this.postingForm().controls.mainVacancyPhoto.markAsDirty();
      this.postingForm().controls.mainVacancyPhoto.setValue(fileList[0]);
    }
  }

  public mainVacancyPhotoRemoved(): void {
    this.postingForm().controls.mainVacancyPhoto.markAsDirty();
    this.postingForm().controls.mainVacancyPhoto.setValue(null);
  }

  public additionalPhotosInputChanged(fileList: FileList | null): void {
    if (fileList?.[0] && !this.additionalPhotosFull) {
      this.postingForm().controls.additionalPhotosToAdd.markAsDirty();
      const newAdditionalPhotosToAdd = this.postingForm().value.additionalPhotosToAdd.concat(
        fileList[0],
      );
      this.postingForm().controls.additionalPhotosToAdd.setValue(newAdditionalPhotosToAdd);
    }
  }

  public postingAdditionalPhotoRemoved(name: string): void {
    this.postingForm().controls.additionalPhotosToDrop.markAsDirty();
    const newAdditionalPhotosToDrop = this.postingForm().value.additionalPhotosToDrop.concat(name);
    this.postingForm().controls.additionalPhotosToDrop.setValue(newAdditionalPhotosToDrop);
  }

  public draftAdditionalPhotoRemoved(index: number): void {
    const newAdditionalPhotosToAdd = this.postingForm().value.additionalPhotosToAdd.slice();
    newAdditionalPhotosToAdd.splice(index, 1);
    this.postingForm().controls.additionalPhotosToAdd.setValue(newAdditionalPhotosToAdd);
  }

  public get additionalPhotosFull(): boolean {
    const posting = this.posting();
    const existentPhotoCount = posting ? posting.additionalPhotos.length : 0;
    return (
      existentPhotoCount -
        this.postingForm().value.additionalPhotosToDrop.length +
        this.postingForm().value.additionalPhotosToAdd.length >=
      this.MAX_ADDITIONAL_PHOTO_COUNT
    );
  }

  public trackByAttachment(index: number, _attachment: Attachment): number {
    return index;
  }

  public get mainVacancyPhotoAttachment(): Attachment | null {
    return this.postingForm().controls.mainVacancyPhoto.dirty
      ? this.state.mainVacancyPhotoAttachment
      : this.postingMainVacancyPhotoAttachment;
  }

  public get postingMainVacancyPhotoAttachment(): Attachment | null {
    const posting = this.posting();
    if (!posting) {
      return null;
    }

    return <any>{
      filename: posting.mainVacancyPhoto.name,
      fileUrl: posting.mainVacancyPhoto.url,
      imageUrl: posting.mainVacancyPhoto.url,
      imageThumbnailUrl: posting.mainVacancyPhoto.url,
    };
  }

  public get postingAdditionalPhotoAttachments(): Attachment[] {
    const posting = this.posting();
    if (!posting) {
      return [];
    }

    return posting.additionalPhotos
      .filter(
        photo =>
          !this.postingForm().value.additionalPhotosToDrop.some((name: any) => photo.name === name),
      )
      .map(
        photo =>
          <any>{
            filename: photo.name,
            fileUrl: photo.url,
            imageUrl: photo.url,
            imageThumbnailUrl: photo.url,
          },
      );
  }

  private initLocations(): void {
    this.werkenInEenHotelApi
      .get('locations')
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        (data: { locations: Location[] }) => {
          this.setState({ ...this.state, locations: data.locations });
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
        },
      );
  }

  private reloadResources(): void {
    const locationId = this.postingForm().get('locationId')!.value;
    if (!locationId) return;

    this.setState({ ...this.state, contacts: [], errors: [] });

    this.werkenInEenHotelApi
      .get(`locations/${locationId}/resources`)
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        (data: { resources: any }) => {
          this.setState({
            ...this.state,
            contacts: data.resources.contacts,
            employmentTypes: data.resources.employmentTypes,
            features: data.resources.features,
            jobCategories: data.resources.jobCategories,
          });
        },
        ({ error }) => {
          this.setState({ ...this.state, errors: error.error });
          this.postingForm().get('locationId')!.setErrors({ bad: true });
        },
      );
  }

  private generatePhotoPreviews(): void {
    this.postingForm()
      .controls.mainVacancyPhoto.valueChanges.pipe(
        takeUntilDestroyed(this),
        tap(photo => {
          if (!photo) {
            this.setState({ ...this.state, mainVacancyPhotoAttachment: null });
          }
        }),
        filterBoolean,
        switchMap(photo => fileToBase64(photo).pipe(map(preview => ({ photo, preview })))),
        tap(({ photo, preview }) => {
          // Not all Attachment type fields are actually required.
          const attachment: Attachment = <any>{
            filename: photo.name,
            fileUrl: preview,
            imageUrl: preview,
            imageThumbnailUrl: preview,
          };

          this.setState({ ...this.state, mainVacancyPhotoAttachment: attachment });
        }),
        // If fileToBase64 fails, just ignore it, it should never happen really.
        catchError(() => EMPTY),
      )
      .subscribe();

    this.postingForm()
      .controls.additionalPhotosToAdd.valueChanges.pipe(
        takeUntilDestroyed(this),
        tap(photos => {
          if (photos.length === 0) {
            this.setState({ ...this.state, draftAdditionalPhotoAttachments: [] });
          }
        }),
        switchMap(photos =>
          forkJoin(
            photos.map((photo: any) =>
              fileToBase64(photo).pipe(map(preview => ({ photo, preview }))),
            ),
          ),
        ),
        tap((photosWithPreviews: any[]) => {
          // Not all Attachment type fields are actually required.
          const attachments: Attachment[] = photosWithPreviews.map(
            ({ photo, preview }: any) =>
              <any>{
                filename: photo.name,
                fileUrl: preview,
                imageUrl: preview,
                imageThumbnailUrl: preview,
              },
          );

          this.setState({ ...this.state, draftAdditionalPhotoAttachments: attachments });
        }),
        // If fileToBase64 fails, just ignore it, it should never happen really.
        catchError(() => EMPTY),
      )
      .subscribe();
  }
}
